/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

if (!customElements.get('apply-filters')) {
  class ApplyFilters extends HTMLElement {
    constructor() {
      super();
      this.applyButton = this.querySelector('#applyButton');
      this.formError = this.querySelector('#form_error');
      this.dropdowns = this.querySelectorAll('.easy_system_filter-select');

      if (this.applyButton) {
        this.applyButton.addEventListener('click', this.applyFilters.bind(this));
      }
    }

    applyFilters() {
      let query = '';
      const collectionHandle = this.getAttribute('data-collection');
      let isValid = true;

      this.dropdowns.forEach((dropdown) => {
        const filterQuery = dropdown.getAttribute('data-filter-query');
        const isRequired = dropdown.getAttribute('data-required') === 'required';

        const selectedOption = dropdown.selectedOptions[0];
        const selectedValue = selectedOption ? selectedOption.value : '';
        const dataRc = selectedOption ? selectedOption.getAttribute('data-rc') : '';

        // ✅ Required dropdown validation
        if (isRequired && (dataRc === 'please select' || !selectedValue)) {
          isValid = false;
          return;
        }

        // ✅ Optional dropdown → add only if selected
        if (selectedValue && dataRc !== 'please select') {
          query += `${filterQuery}${selectedValue}`;
        }
      });

      if (this.dropdowns.length > 0) {
        if (!isValid) {
          if (this.formError) this.formError.style.display = 'block';
          return;
        }

        if (this.formError) this.formError.style.display = 'none';

        const url = query
          ? `/collections/${collectionHandle}?${query}`
          : `/collections/${collectionHandle}`;

        window.location.href = url;
      } else {
        window.location.href = `/collections/${collectionHandle}`;
      }
    }
  }

  customElements.define('apply-filters', ApplyFilters);
}


if (!customElements.get('custom-roof-curb-add-to-cart-button')) {
  class CustomRoofCurbAddToCartButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');

      // Find closest card and popup inside it
      this.card = this.closest('.card');
      this.popup = this.card?.querySelector('.roof-curb-popup');

      this.closeBtn = this.popup?.querySelector('.roof-curb-popup__close');
      this.confirmBtn = this.popup?.querySelector('.roof-curb-confirm');

      this.onButtonClick = this.onButtonClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onConfirm = this.onConfirm.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
    }

    connectedCallback() {
      if (!this.button || !this.popup) return;

      this.button.addEventListener('click', this.onButtonClick);
      this.closeBtn?.addEventListener('click', this.onClose);
      this.confirmBtn?.addEventListener('click', this.onConfirm);
      this.popup.addEventListener('click', this.onBackdropClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onButtonClick);
      this.closeBtn?.removeEventListener('click', this.onClose);
      this.confirmBtn?.removeEventListener('click', this.onConfirm);
      this.popup?.removeEventListener('click', this.onBackdropClick);
    }

    onButtonClick(e) {
      e.preventDefault();
      this.openPopup();
    }

    openPopup() {
      this.popup.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    closePopup() {
      this.popup.classList.remove('active');
      document.body.style.overflow = '';
    }

    onClose() {
      this.closePopup();
    }

    onBackdropClick(e) {
      if (e.target === this.popup) {
        this.closePopup();
      }
    }

    onConfirm() {
      this.closePopup();

      // Submit ONLY this card’s form
      const form = this.closest('form');
      form?.submit();
    }
  }

  customElements.define('custom-roof-curb-add-to-cart-button', CustomRoofCurbAddToCartButton);
}


if (!customElements.get('easy-system-selector')) {
  class EasySystemSelector extends HTMLElement {
    constructor() {
      super();
      this.informationIcons = this.querySelectorAll('.information_icon');
      this.modals = this.querySelectorAll('.information_icon-model');
      this.closeButtons = this.querySelectorAll('.information_icon-model-close-btn, .information_icon-model-bg-overlay');
      this.accordionHeaders = this.querySelectorAll('.accordion_question');
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this.toggleAccordion = this.toggleAccordion.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.informationIcons.forEach(icon => {
        icon.addEventListener('click', this.openModal);
      });

      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', this.closeModal);
      });

      this.handleResize();
      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      this.informationIcons.forEach(icon => {
        icon.removeEventListener('click', this.openModal);
      });

      this.closeButtons.forEach(btn => {
        btn.removeEventListener('click', this.closeModal);
      });

      this.accordionHeaders.forEach(header => {
        header.removeEventListener('click', this.toggleAccordion);
      });

      window.removeEventListener('resize', this.handleResize);
    }

    openModal(e) {
      const target = this.querySelector(e.currentTarget.getAttribute('data-target'));
      if (target) {
        target.classList.add('information_icon-model-open');
      }
    }

    closeModal(e) {
      const modal = e.currentTarget.closest('.information_icon-model');
      if (modal) {
        modal.classList.remove('information_icon-model-open');
      }
    }

    setupAccordion() {
      this.accordionHeaders.forEach(header => {
        header.addEventListener('click', this.toggleAccordion);
      });
    }

    toggleAccordion(e) {
      const header = e.currentTarget;
      header.classList.toggle('active');
      const content = header.nextElementSibling;
      if (content.style.maxHeight) {
        content.style.maxHeight = null;
      } else {
        content.style.maxHeight = "100%";
      }
    }

    handleResize() {
      if (window.innerWidth <= 1024) {
        this.setupAccordion();
      } else {
        this.accordionHeaders.forEach(header => {
          header.classList.remove('active');
          const content = header.nextElementSibling;
          content.style.maxHeight = null;
        });
      }
    }
  }
  customElements.define('easy-system-selector', EasySystemSelector);
}

if (!customElements.get('hvac-quick-search')) {
  class HvacQuickSearch extends HTMLElement {
    constructor() {
      super();
      this.collection = this.querySelector('#collection');
      this.indoor_units_option = this.querySelector('#indoor_units_option');
      this.tonnage = this.querySelector('#tonnage');
      this.furnaces_btu = this.querySelector('#furnaces_btu');
      this.afue = this.querySelector('#afue');
      this.roof_top_packaged = this.querySelector('#roof_top_packaged');
      this.zones_rooms = this.querySelector('#zones_rooms');
      this.indoor_units1 = this.querySelector('#indoor_units1');
      this.indoor_units2 = this.querySelector('#indoor_units2');
      this.indoor_units3 = this.querySelector('#indoor_units3');
      this.indoor_units4 = this.querySelector('#indoor_units4');
      this.indoor_units5 = this.querySelector('#indoor_units5');
      this.indoor_units6 = this.querySelector('#indoor_units6');
      this.indoor_units7 = this.querySelector('#indoor_units7');
      this.indoor_units8 = this.querySelector('#indoor_units8');
      this.mini_split_type = this.querySelector('#mini_split_type');
      this.applyButton = this.querySelector('#hvac_quick_search_Button');
      this.hvac_quick_error = this.querySelector('#hvacerror');

      if (this.collection) {
        this.collection.addEventListener('change', this.updateVisibility);
      }

      if (this.zones_rooms) {
        this.zones_rooms.addEventListener('change', this.updateVisibilityzonesrooms);
      }

      if (this.applyButton) {
        this.applyButton.addEventListener('click', this.applyFilters.bind(this));
      }
    }

    updateVisibility() {
      const collectionValue = collection.value;
      switch (collectionValue) {
        case 'please-select':
          tonnage.parentElement.style.display = 'none';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'furnace-air-conditioner-systems':
          tonnage.parentElement.style.display = 'block';
          furnaces_btu.parentElement.style.display = 'block';
          afue.parentElement.style.display = 'block';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'furnace-systems':
          tonnage.parentElement.style.display = 'none';
          furnaces_btu.parentElement.style.display = 'block';
          afue.parentElement.style.display = 'block';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'heat-pump-systems':
          tonnage.parentElement.style.display = 'block';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'mini-split-systems':
          tonnage.parentElement.style.display = 'none';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'block';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'heat-pump-condensers':
          tonnage.parentElement.style.display = 'block';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'air-conditioner-condenser-units':
          tonnage.parentElement.style.display = 'block';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'none';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
        case 'packaged-units':
          tonnage.parentElement.style.display = 'none';
          furnaces_btu.parentElement.style.display = 'none';
          afue.parentElement.style.display = 'none';
          roof_top_packaged.parentElement.style.display = 'block';
          zones_rooms.parentElement.style.display = 'none';
          indoor_units_option.style.display = 'none';
          mini_split_type.parentElement.style.display = 'none';
          break;
      }
    }
    updateVisibilityzonesrooms() {
      const zonesroomsvalue = zones_rooms.value;
      indoor_units_option.style.display = 'block';
      mini_split_type.parentElement.style.display = 'block';
      this.querySelectorAll('.indoor_units').forEach(option => {
        option.style.display = 'none';
      });
      switch (zonesroomsvalue) {
        case 'please-select':
          indoor_units1.style.display = 'none';
          indoor_units2.style.display = 'none';
          indoor_units3.style.display = 'none';
          indoor_units4.style.display = 'none';
          indoor_units5.style.display = 'none';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'single-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'none';
          indoor_units3.style.display = 'none';
          indoor_units4.style.display = 'none';
          indoor_units5.style.display = 'none';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'dual-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'none';
          indoor_units4.style.display = 'none';
          indoor_units5.style.display = 'none';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'three-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'none';
          indoor_units5.style.display = 'none';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'four-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'block';
          indoor_units5.style.display = 'none';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'five-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'block';
          indoor_units5.style.display = 'block';
          indoor_units6.style.display = 'none';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'six-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'block';
          indoor_units5.style.display = 'block';
          indoor_units6.style.display = 'block';
          indoor_units7.style.display = 'none';
          indoor_units8.style.display = 'none';
          break;
        case 'seven-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'block';
          indoor_units5.style.display = 'block';
          indoor_units6.style.display = 'block';
          indoor_units7.style.display = 'block';
          indoor_units8.style.display = 'none';
          break;
        case 'eight-zone':
          indoor_units1.style.display = 'block';
          indoor_units2.style.display = 'block';
          indoor_units3.style.display = 'block';
          indoor_units4.style.display = 'block';
          indoor_units5.style.display = 'block';
          indoor_units6.style.display = 'block';
          indoor_units7.style.display = 'block';
          indoor_units8.style.display = 'block';
          break;
      }
    }
    applyFilters() {

      if (collection.value === 'mini-split-systems') {

        let indoor_units_Values = [];
        const mini_split_collection_handle = collection.value;
        const zonesroomsvalue = zones_rooms.value;
        const indoor_units1Value = indoor_units1.selectedOptions[0].getAttribute('data-rc');
        const indoor_units2Value = indoor_units2.selectedOptions[0].getAttribute('data-rc');
        const indoor_units3Value = indoor_units3.selectedOptions[0].getAttribute('data-rc');
        const indoor_units4Value = indoor_units4.selectedOptions[0].getAttribute('data-rc');
        const indoor_units5Value = indoor_units5.selectedOptions[0].getAttribute('data-rc');
        const indoor_units6Value = indoor_units6.selectedOptions[0].getAttribute('data-rc');
        const indoor_units7Value = indoor_units7.selectedOptions[0].getAttribute('data-rc');
        const indoor_units8Value = indoor_units8.selectedOptions[0].getAttribute('data-rc');

        switch (zonesroomsvalue) {
          case 'single-zone':
            indoor_units_Values.push(indoor_units1Value);
            break;
          case 'dual-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            break;
          case 'three-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            break;
          case 'four-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            indoor_units_Values.push(indoor_units4Value);
            break;
          case 'five-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            indoor_units_Values.push(indoor_units4Value);
            indoor_units_Values.push(indoor_units5Value);
            break;
          case 'six-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            indoor_units_Values.push(indoor_units4Value);
            indoor_units_Values.push(indoor_units5Value);
            indoor_units_Values.push(indoor_units6Value);
            break;
          case 'seven-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            indoor_units_Values.push(indoor_units4Value);
            indoor_units_Values.push(indoor_units5Value);
            indoor_units_Values.push(indoor_units6Value);
            indoor_units_Values.push(indoor_units7Value);
            break;
          case 'eight-zone':
            indoor_units_Values.push(indoor_units1Value);
            indoor_units_Values.push(indoor_units2Value);
            indoor_units_Values.push(indoor_units3Value);
            indoor_units_Values.push(indoor_units4Value);
            indoor_units_Values.push(indoor_units5Value);
            indoor_units_Values.push(indoor_units6Value);
            indoor_units_Values.push(indoor_units7Value);
            indoor_units_Values.push(indoor_units8Value);
            break;
        }

        indoor_units_Values.sort((a, b) => parseInt(a) - parseInt(b));
        const query = indoor_units_Values.join('%2B');
        const query_filter = `filter.p.m.custom.indoor_unit_btus=${query}&`;

        let mini_split_type_filter = '';
        if (mini_split_type.parentElement.style.display = 'block') {
          const mini_split_typeValue = mini_split_type.selectedOptions[0].getAttribute('data-rc');
          if (mini_split_typeValue !== 'please-select') {
            mini_split_type_filter += `filter.p.m.custom.mount_type=${mini_split_typeValue}`;
          }
        }
        const final_query = query_filter + mini_split_type_filter
        const url = `/collections/${mini_split_collection_handle}?${final_query}`;
        window.location.href = url;

      } else {

        let newquery = '';
        const collection_handle = collection.value;

        if (tonnage.parentElement.style.display === 'block') {
          const tonnageValue = tonnage.selectedOptions[0].getAttribute('data-rc');
          if (tonnageValue === 'please-select') {
            this.hvac_quick_error.style.display = 'block';
            return;
          }
          newquery += `filter.p.m.custom.system_tonnage=${tonnageValue}+Ton&`;
        }

        if (furnaces_btu.parentElement.style.display === 'block') {
          const furnaces_btuValue = furnaces_btu.selectedOptions[0].getAttribute('data-rc');
          if (furnaces_btuValue === 'please-select') {
            this.hvac_quick_error.style.display = 'block';
            return;
          }
          newquery += `filter.p.m.Goodman.btu_furnace=${furnaces_btuValue}+BTU%27s&`;
        }

        if (afue.parentElement.style.display === 'block') {
          const afueValue = afue.selectedOptions[0].getAttribute('data-rc');
          if (afueValue === 'please-select') {
            newquery += '';
          }
          newquery += `filter.p.m.custom.furnace_efficiency_afue_=${afueValue}&`;
        }

        if (roof_top_packaged.parentElement.style.display === 'block') {
          const roof_top_packagedValue = roof_top_packaged.selectedOptions[0].getAttribute('data-rc');
          if (roof_top_packagedValue === 'please-select') {
            this.hvac_quick_error.style.display = 'block';
            return;
          }
          newquery += `filter.p.m.custom.system_roof_top_packaged=${roof_top_packagedValue}&`;
        }

        if (!newquery) {
          this.hvac_quick_error.style.display = 'block';
          return;
        }

        const url = `/collections/${collection_handle}?${newquery}`;
        window.location.href = url;

      }
    }
  }
  customElements.define('hvac-quick-search', HvacQuickSearch);
}

if (!customElements.get('lift-gate-service')) {
  class LiftGateService extends HTMLElement {
    constructor() {
      super();

      this.liftGateOptions = this.querySelectorAll("input[name='lift_gate']");
      this.liftGateVariantId = parseInt(this.getAttribute("data-variant-id"), 10);
      this.insuranceVariantId = parseInt(this.getAttribute("data-variant-id-insurance"), 10);

      this.updateCart = this.updateCart.bind(this);
      this.checkAndRemoveLiftGate = this.checkAndRemoveLiftGate.bind(this);
      this.attachRemoveListeners = this.attachRemoveListeners.bind(this);
    }

    connectedCallback() {
      this.liftGateOptions.forEach(option => {
        option.addEventListener("change", (e) => this.updateCart(e.target.value));
      });

      this.checkCartStatus();
      this.attachRemoveListeners();

      const infoButton = this.querySelector(".what_is_liftgate");

      if (infoButton) {
        infoButton.addEventListener("click", () => this.openLiftGateModal());
      }
    }

    async updateCart(value) {
      const cart = await this.getCart();
      const liftGateItem = cart.items.find(item =>
        item.variant_id === this.liftGateVariantId || item.variant_id === this.insuranceVariantId
      );

      if (liftGateItem) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: liftGateItem.key, quantity: 0 })
        });
      }

      if (value === "Yes" || value === "Insurance") {
        const variantToAdd = value === "Yes" ? this.liftGateVariantId : this.insuranceVariantId;
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantToAdd, quantity: 1 })
          });
        } catch (error) {
          console.error("Error adding lift gate service:", error);
        }
      }

      location.reload();
    }

    async checkCartStatus() {
      try {
        const cart = await this.getCart();
        const liftGateItem = cart.items.find(item =>
          item.variant_id === this.liftGateVariantId || item.variant_id === this.insuranceVariantId
        );

        if (liftGateItem?.variant_id === this.liftGateVariantId) {
          document.getElementById("lift_gate_yes").checked = true;
        } else if (liftGateItem?.variant_id === this.insuranceVariantId) {
          document.getElementById("lift_gate_insurance").checked = true;
        } else {
          document.getElementById("lift_gate_no").checked = true;
        }
      } catch (error) {
        console.error("Error checking cart:", error);
      }
    }

    async checkAndRemoveLiftGate() {
      try {
        const cart = await this.getCart();
        const mainProducts = cart.items.filter(item =>
          item.variant_id !== this.liftGateVariantId && item.variant_id !== this.insuranceVariantId
        );
        const liftGateItem = cart.items.find(item =>
          item.variant_id === this.liftGateVariantId || item.variant_id === this.insuranceVariantId
        );

        if (mainProducts.length === 0 && liftGateItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: liftGateItem.key, quantity: 0 })
          });
          setTimeout(() => location.reload(), 500);
          return;
        }

        const updatedCart = await this.getCart();
        const updatedLiftGateItem = updatedCart.items.find(item =>
          item.variant_id === this.liftGateVariantId || item.variant_id === this.insuranceVariantId
        );

        if (updatedLiftGateItem?.variant_id === this.liftGateVariantId) {
          document.getElementById("lift_gate_yes").checked = true;
        } else if (updatedLiftGateItem?.variant_id === this.insuranceVariantId) {
          document.getElementById("lift_gate_insurance").checked = true;
        } else {
          document.getElementById("lift_gate_no").checked = true;
        }

      } catch (error) {
        console.error("Error checking/removing lift gate:", error);
      }
    }

    attachRemoveListeners() {
      document.querySelectorAll(".js-remove-item").forEach(button => {
        button.addEventListener("click", async (event) => {
          event.preventDefault();
          const removeUrl = button.getAttribute("href");
          try {
            await fetch(removeUrl, { method: "GET" });
            await this.checkAndRemoveLiftGate();
            setTimeout(() => {
              if (typeof updateCartBubble === "function") updateCartBubble();
            }, 500);
            this.attachRemoveListeners();
          } catch (error) {
            console.error("Error removing item:", error);
          }
        });
      });
    }

    async getCart() {
      const response = await fetch('/cart.js');
      return await response.json();
    }

    openLiftGateModal() {
      const liftgate_modalHTML = `
        <div class="liftgate-modal">
          <div class="liftgate-modal-overlay"></div>
          <div class="liftgate-modal-content">
            <button class="liftgate-modal-close"><svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"/></svg></button>
            <h5 class="h5">The lift gate option is recommended.</h5>
            <p>Without the lift gate option you are responsible for unloading the truck on your own.</p>
            <div class="liftgate-video">
              <video controls width="100%">
                <source src="https://cdn.shopify.com/videos/c/o/v/3e9df4367c2748728cd82eba4fc51436.mp4" type="video/mp4">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML("beforeend", liftgate_modalHTML);
      const liftgate_modal = document.querySelector(".liftgate-modal");
      const liftgate_modal_closeBtn = liftgate_modal.querySelector(".liftgate-modal-close");
      const liftgate_modal_overlay = liftgate_modal.querySelector(".liftgate-modal-overlay");

      liftgate_modal.style.display = "flex";
      document.body.style.overflow = "hidden";

      const closeModal = () => {
        liftgate_modal.remove();
        document.body.style.overflow = "";
      };

      liftgate_modal_closeBtn.addEventListener("click", closeModal);
      liftgate_modal_overlay.addEventListener("click", closeModal);
    }
  }

  customElements.define('lift-gate-service', LiftGateService);
}


if (!customElements.get('flat-bed-shipping')) {
  class FlatBedShipping extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector("#flat_bad_shipping");
      this.variantId = parseInt(this.getAttribute("data-variant-id"), 10);
      this.requiredPackageUnits = [
        "Light Commercial Packaged Air Conditioner",
        "Light Commercial Packaged Gas/Electric",
        "Light Commercial Packaged Heat Pump"
      ];
      this.flatbedRequired = false;

      this.evaluateRequirement = this.evaluateRequirement.bind(this);
      this.updateFlatbedShipping = this.updateFlatbedShipping.bind(this);
      this.syncWithCart = this.syncWithCart.bind(this);
      this.checkAndRemoveFlatbed = this.checkAndRemoveFlatbed.bind(this);
      this.attachRemoveListeners = this.attachRemoveListeners.bind(this);
    }

    connectedCallback() {
      if (!this.checkbox) return;

      this.checkbox.addEventListener("change", () => {
        if (!this.flatbedRequired) {
          this.updateFlatbedShipping(this.checkbox.checked);
        }
      });

      this.syncWithCart();
      this.attachRemoveListeners();
    }

    async getCart() {
      const response = await fetch("/cart.js");
      return await response.json();
    }

    async evaluateRequirement() {
      const cart = await this.getCart();
      this.flatbedRequired = false;

      cart.items.forEach(item => {
        const tonnage = parseFloat(item.properties?.tonnage || 0);
        const packageUnit = item.properties?.packageUnit?.trim();
        if (this.requiredPackageUnits.includes(packageUnit) && tonnage >= 7.5) {
          this.flatbedRequired = true;
        }
      });

      if (this.flatbedRequired) {
        this.checkbox.checked = true;
        this.checkbox.disabled = true;

        const hasFlatBed = cart.items.some(item => item.variant_id === this.variantId);
        if (!hasFlatBed) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: this.variantId, quantity: 1 })
          });
          location.reload();
        }
      }
    }

    async updateFlatbedShipping(checked) {
      if (checked) {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: this.variantId, quantity: 1 })
        });
        location.reload();
      } else {
        const cart = await this.getCart();
        const flatBedItem = cart.items.find(item => item.variant_id === this.variantId);
        if (flatBedItem) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: flatBedItem.key, quantity: 0 })
          });
        }
        location.reload();
      }
    }

    async syncWithCart() {
      const cart = await this.getCart();
      const hasFlatBed = cart.items.some(item => item.variant_id === this.variantId);
      this.checkbox.checked = hasFlatBed;
      await this.evaluateRequirement();
    }

    attachRemoveListeners() {
      document.querySelectorAll(".js-remove-item").forEach(button => {
        button.addEventListener("click", async (event) => {
          event.preventDefault();
          const removeUrl = button.getAttribute("href");
          try {
            await fetch(removeUrl, { method: "GET" });
            await this.checkAndRemoveFlatbed();
            setTimeout(() => {
              if (typeof updateCartBubble === "function") {
                updateCartBubble();
              }
            }, 500);
            this.attachRemoveListeners();
          } catch (error) {
            console.error("Error removing item (flatbed):", error);
          }
        });
      });
    }

    async checkAndRemoveFlatbed() {
      const cart = await this.getCart();
      const flatBedItem = cart.items.find(item => item.variant_id === this.variantId);
      const mainProducts = cart.items.filter(item => item.variant_id !== this.variantId);

      let stillRequired = false;
      mainProducts.forEach(item => {
        const tonnage = parseFloat(item.properties?.tonnage || 0);
        const packageUnit = item.properties?.packageUnit?.trim() || "";
        if (this.requiredPackageUnits.includes(packageUnit) && tonnage >= 7.5) {
          stillRequired = true;
        }
      });

      if (!stillRequired && flatBedItem) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: flatBedItem.key, quantity: 0 })
        });
        setTimeout(() => location.reload(), 500);
      }
    }
  }

  customElements.define("flat-bed-shipping", FlatBedShipping);
}


if (!customElements.get('zone-filters')) {
  class ZoneFilters extends HTMLElement {
    constructor() {
      super();

      // Dropdown references
      this.dropdown1 = this.querySelector('#dropdown1');
      this.dropdown2 = this.querySelector('#dropdown2');
      this.dropdown3 = this.querySelector('#dropdown3');
      this.dropdown4 = this.querySelector('#dropdown4');
      this.dropdown5 = this.querySelector('#dropdown5');
      this.applyButton = this.querySelector('#applyButton');
      this.filter_error = this.querySelector('#filter_error');

      if (this.applyButton) {
        this.applyButton.addEventListener('click', this.applyFilters.bind(this));
      }
    }

    getQueryParam(name) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(name);
    }

    applyFilters() {
      const templateSuffix = this.dataset.templateSuffix; // from Liquid
      const collectionHandle = this.dataset.collection; // from data attribute
      const filter_error = this.filter_error;
      filter_error.style.display = 'none';

      if (templateSuffix === 'single-zone-mini-split-systems') {
        this.handleSingleZone(collectionHandle);
      } else {
        this.handleMultiZone(collectionHandle);
      }
    }

    handleSingleZone(collectionHandle) {
      const dropdown1Value = this.dropdown1?.selectedOptions[0]?.getAttribute('data-rc');
      const dropdown2Value = this.dropdown2?.selectedOptions[0]?.getAttribute('data-rc');
      const filter_error = this.filter_error;

      // Validation
      if (!dropdown1Value || dropdown1Value === 'please select') {
        filter_error.style.display = 'block';
        return;
      }

      // Build query
      let query = `filter.p.m.custom.indoor_unit_btus=${dropdown1Value}`;
      if (dropdown2Value && dropdown2Value !== 'please select') {
        query += `&filter.p.m.custom.mount_type=${dropdown2Value}`;
      }

      // Redirect
      const path = this.getQueryParam('path');
      this.redirectToUrl(collectionHandle, path, query);
    }

    handleMultiZone(collectionHandle) {
      let values = [];
      const filter_error = this.filter_error;

      const dropdowns = [
        this.dropdown1,
        this.dropdown2,
        this.dropdown3,
        this.dropdown4,
        this.dropdown5,
      ];

      // Collect valid dropdown values
      for (const dropdown of dropdowns) {
        if (dropdown) {
          const value = dropdown.selectedOptions[0]?.getAttribute('data-rc');
          if (!value || value === 'please select') {
            filter_error.style.display = 'block';
            return;
          }
          values.push(value);
        }
      }

      if (values.length === 0) {
        filter_error.style.display = 'block';
        return;
      }

      // Sort like your Liquid code
      values.sort((a, b) => parseInt(a) - parseInt(b));

      // Build same query format: filter.p.m.custom.indoor_unit_btus=9K%2B12K%2B18K
      const query = `filter.p.m.custom.indoor_unit_btus=${values.join('%2B')}`;

      // Redirect
      const path = this.getQueryParam('path');
      this.redirectToUrl(collectionHandle, path, query);
    }

    redirectToUrl(collectionHandle, path, query) {
      let vendor = '';

      if (path === 'mrcool') {
        vendor = 'filter.p.vendor=MR+COOL';
      } else if (path === 'goodman-daikin') {
        vendor = 'filter.p.vendor=Daikin&filter.p.vendor=Goodman';
      } else if (path === 'mitsubishi') {
        vendor = 'filter.p.vendor=Mitsubishi';
      } else {
        return;
      }

      // ✅ EXACT same as your working inline version
      const url = `/collections/${collectionHandle}?${vendor}&${query}`;
      window.location.href = url;
    }
  }

  customElements.define('zone-filters', ZoneFilters);
}


if (!customElements.get('size-estimator')) {
  class SizeEstimator extends HTMLElement {
    constructor() {
      super();

      // Element references
      this.form = this.querySelector('#size-estimator-form');
      this.submitBtn = this.form?.querySelector('#size-estimator-submit-btn');
      this.squareFeetInput = this.form?.querySelector('#square_footage');

      // Result elements
      this.heading = this.querySelector('#size-estimator-heading');
      this.resultDiv = this.querySelector('#estimate-result');
      this.resultText = this.querySelector('#estimate-result-content');
      this.coolingTitle = this.querySelector('#cooling-product-title');
      this.heatingTitle = this.querySelector('#heating-product-title');

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', this.handleSubmit.bind(this));
      }
    }

    // Calculate Cooling BTUs
    calculateCoolingBTUs() {
      const squareFeet = parseFloat(this.squareFeetInput.value);
      if (squareFeet > 0) {
        const BTUs = squareFeet * 20;
        const tons = BTUs / 12000;
        const roundedBTUs = Math.round(BTUs / 1000) * 1000;
        const roundedTons = Math.round(tons);
        return { roundedBTUs, roundedTons };
      }
      return null;
    }

    // Calculate Heating BTUs
    calculateHeatingBTUs() {
      const squareFeet = parseFloat(this.squareFeetInput.value);
      if (squareFeet > 0) {
        const BTUs = squareFeet * 35;
        const roundedBTUs = Math.round(BTUs / 1000) * 1000;
        return roundedBTUs;
      }
      return null;
    }

    // Handle submit
    handleSubmit(event) {
      event.preventDefault();

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const cooling = this.calculateCoolingBTUs();
      const heating = this.calculateHeatingBTUs();

      if (!cooling || !heating) return;

      // Update UI
      this.heading.innerHTML = 'Your Sizing Results';
      this.form.style.display = 'none';
      this.resultText.innerHTML = `For a ${this.squareFeetInput.value} Sq. Ft. Space`;
      this.coolingTitle.innerHTML = `Cooling: ${cooling.roundedTons} Tons (${cooling.roundedBTUs} BTUs)`;
      this.heatingTitle.innerHTML = `Heating: ${heating} Output/Effective BTUs`;
      this.resultDiv.style.display = 'block';
    }
  }

  customElements.define('size-estimator', SizeEstimator);
}

if (!customElements.get('additional-facet-filters')) {
  class FacetFilters extends HTMLElement {
    constructor() {
      super();

      this.tabButtons = this.querySelectorAll('.filter_tab-button');
      this.tabContents = this.querySelectorAll('.filter_tab-content');

      this.tabButtons.forEach(button => {
        button.addEventListener('click', this.switchTab.bind(this));
      });
    }

    switchTab(event) {
      const targetTabId = event.target.getAttribute('data-target');

      this.tabButtons.forEach(button => {
        button.classList.remove('active');
      });

      this.tabContents.forEach(content => {
        content.style.display = 'none';
      });

      event.target.classList.add('active');
      this.querySelector(`[id='${targetTabId}']`).style.display = 'grid';
    }

    connectedCallback() {
      if (this.tabButtons.length > 0) {
        this.tabButtons[0].classList.add('active');
        this.tabContents[0].style.display = 'grid';
      }
    }
  }

  customElements.define('additional-facet-filters', FacetFilters);
}

class CartShare extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('#share-cart');
    this.baseUrl = window.location.origin;
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.handleShareClick.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleShareClick.bind(this));
    }
  }

  async handleShareClick() {
    try {
      const cart = await this.fetchCart();
      const shareUrl = this.generateCartUrl(cart);
      await this.copyToClipboard(shareUrl);
      this.showPopup(shareUrl);
    } catch (error) {
      console.error('Share Cart Error:', error);
      this.showPopup(null, true);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart.');
    return await response.json();
  }

  generateCartUrl(cart) {
    const items = cart.items.map(item => `${item.variant_id}:${item.quantity}`).join(',');
    return `${this.baseUrl}/cart/${items}`;
  }

  async copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  showPopup(shareUrl, isError = false) {
    const oldPopup = document.querySelector('.cart-share__popup');
    if (oldPopup) oldPopup.remove();

    const popup = document.createElement('div');
    popup.className = 'cart-share__popup';
    const message = isError
      ? '⚠️ Sorry, something went wrong.'
      : `✅ Cart link copied! <br><a href="${shareUrl}" target="_blank" class="cart-share__link">${shareUrl}</a>`;

    popup.innerHTML = `
      <div class="cart-share__popup-inner ${isError ? 'cart-share__popup--error' : ''}">
        <span class="cart-share__popup-close" role="button" aria-label="Close">&times;</span>
        <p class="cart-share__popup-message">${message}</p>
      </div>
    `;

    document.body.appendChild(popup);

    setTimeout(() => popup.classList.add('cart-share__popup--visible'), 10);

    popup.querySelector('.cart-share__popup-close').addEventListener('click', () => this.hidePopup(popup));
    setTimeout(() => this.hidePopup(popup), 5000);
  }

  hidePopup(popup) {
    popup.classList.remove('cart-share__popup--visible');
    setTimeout(() => popup.remove(), 300);
  }
}

customElements.define('cart-share', CartShare);

class EmptyCartButton extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <button type="button" class="btn btn--primary btn--icon-with-text btn--empty-cart">
        <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"/></svg>
        Empty Cart
      </button>
    `;
  }

  connectedCallback() {
    const button = this.querySelector('button');
    if (button) {
      button.addEventListener('click', () => this.clearCart());
    }
  }

  clearCart() {
    // if (!confirm('Are you sure you want to empty your cart?')) return;

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
      .then(res => res.json())
      .then(() => {
        window.location.reload();
      })
      .catch(err => console.error('Error clearing cart:', err));
  }
}

customElements.define('empty-cart-btn', EmptyCartButton);


if (!customElements.get('expedited-shipping')) {
  class ExpeditedShipping extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.checkbox = this.querySelector('.expedited_shipping');
      this.expeditedVariantId = this.checkbox?.value;
      this.form = this.closest('form');
      this.submitButton = this.form?.querySelector('[type="submit"]');

      if (!this.form || !this.checkbox || !this.submitButton) return;

      // Listen to click on the Add to Cart button to run before form submission
      this.submitButton.addEventListener('click', this.handleSubmit, { capture: true });
    }

    async handleSubmit(e) {
      // If checkbox isn't checked, let the normal ProductForm handle the ADD TO CART
      if (!this.checkbox.checked) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const formData = new FormData(this.form);
      const mainVariantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;

      try {
        // Disable submit button while processing
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('is-loading');
        }

        // STEP 1: Prepare items
        const items = [
          {
            id: mainVariantId,
            quantity: quantity
          },
          {
            id: this.expeditedVariantId,
            quantity: 1
          }
        ];

        // STEP 2: Add both products together
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        // STEP 3: Redirect to cart
        window.location.href = '/cart';

      } catch (error) {
        console.error('Expedited Shipping Error:', error);
        if (this.submitButton) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('is-loading');
        }
      }
    }
  }

  customElements.define('expedited-shipping', ExpeditedShipping);
}

class DiscountedPrice extends HTMLElement {
  constructor() {
    super();
    this._trigger = null;
    this._popup = null;
    this._closeBtn = null;
    this._onOutsideClick = this._onOutsideClick.bind(this);
  }

  connectedCallback() {
    this._trigger = this.querySelector('.view-discounted-price-btn');
    this._closeBtn = this.querySelector('.discounted-price-popup__close');

    const popupId = this._trigger?.getAttribute('aria-controls');
    this._popup = popupId ? document.getElementById(popupId) : null;

    // Fallback if ID is missing or not found in global document
    if (!this._popup) {
      this._popup = this.querySelector('.discounted-price-popup');
    }

    if (!this._trigger || !this._popup) return;

    this._trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this._toggle();
    });
    
    this._closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._close();
    });
    
    document.addEventListener('click', this._onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onOutsideClick);
  }

  _toggle() {
    if (!this._popup) return;
    const isOpen = !this._popup.hasAttribute('hidden');
    // Close all other open popups first
    document.querySelectorAll('discounted-price').forEach((el) => {
      if (el !== this) el._close?.();
    });
    isOpen ? this._close() : this._open();
  }

  _open() {
    if (!this._popup) return;
    this._popup.removeAttribute('hidden');
    this._trigger?.setAttribute('aria-expanded', 'true');
  }

  _close() {
    if (!this._popup) return;
    this._popup.setAttribute('hidden', '');
    this._trigger?.setAttribute('aria-expanded', 'false');
  }

  _onOutsideClick(e) {
    if (!this.contains(e.target)) this._close();
  }
}

customElements.define('discounted-price', DiscountedPrice);




class ReadMore {
  constructor(blockId) {
    this.blockId = blockId;
    this.content = document.getElementById(`subtitle_${blockId}`);
    this.btn = document.getElementById(`subtitle_readmore_toggleBtn_${blockId}`);
    this.isExpanded = false;

    if (!this.content || !this.btn) return;

    this.btn.addEventListener('click', () => this.toggle());
    this.init();

    window.addEventListener('resize', () => this.init());
  }

  init() {
    // Reset state
    this.isExpanded = false;
    this.content.classList.remove('expanded');
    this.btn.textContent = 'Read More';
    this.btn.style.display = 'none';

    // Get line height and set CSS variable
    const lineHeight = parseFloat(getComputedStyle(this.content).lineHeight);
    const threeLineHeight = lineHeight * 3;

    // Set CSS variable so max-height uses exact line height
    this.content.style.setProperty('--line-height', `${lineHeight}px`);

    // Temporarily remove max-height to measure full scroll height
    this.content.style.maxHeight = 'none';
    const fullHeight = this.content.scrollHeight;

    // Restore clamped max-height
    this.content.style.maxHeight = '';

    if (fullHeight > threeLineHeight + 2) { // +2 for subpixel tolerance
      this.btn.style.display = 'inline-block';
    }
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
    this.content.classList.toggle('expanded', this.isExpanded);
    this.btn.textContent = this.isExpanded ? 'Read Less' : 'Read More';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-block-id]').forEach(btn => {
    new ReadMore(btn.dataset.blockId);
  });
});